import type { Lot } from '../../types/lot'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import { toProductionRowStage } from '../../utils/lotInteraction'

/**
 * v2.2 T3-D-04b: overlay `Lot.stage` (5-value lifecycle enum) onto each
 * production row so `row.stage` resolves to the LOT'S CURRENT stage rather
 * than the operator-picked / metadata-snapshot value on the row.
 *
 * Rationale (invariant I-1 compliance): `Lot.stage` is the Tier F identity
 * source of truth for lifecycle. The row-level `stage` shipped in
 * T3-D-04a is a snapshot-at-write-time — useful for historical replay but
 * not for "what is the current state of this lot?" answers. This overlay
 * lets any surface that renders `row.stage` show live lot state via a
 * single point of authority, and prepares for T3-D-04c retiring the row
 * column entirely.
 *
 * Semantics:
 * - Rows with a `lotId` matching a Lot in `lots` get `row.stage` replaced
 *   with `toProductionRowStage(lot.stage)` (collapses 5-value LotStage to
 *   the row's 2-value shape). Applied OUTERMOST in the samplingRows chain
 *   so it wins over any `metadata.stage` snapshot the event bridges wrote.
 * - Rows without `lotId` OR without a matching Lot are returned untouched
 *   (referential equality preserved) — critical for React memoization in
 *   the chained overlay pipeline.
 * - Rows where the derived stage already matches the current `row.stage`
 *   are also returned untouched.
 *
 * Chain order in `Biomass.tsx#samplingRows`:
 *   mortality → weight → fishStock → harvest → lotStage (outermost).
 */
export const overlayLotStageOnProductionRows = (
  rows: BiomassDataRow[],
  lots: readonly Pick<Lot, 'id' | 'stage'>[]
): BiomassDataRow[] => {
  if (rows.length === 0 || lots.length === 0) return rows

  const stageByLotId = new Map<string, 'Active' | 'Inactive'>()
  for (const lot of lots) {
    if (typeof lot.id !== 'string' || !lot.id) continue
    stageByLotId.set(lot.id, toProductionRowStage(lot.stage))
  }
  if (stageByLotId.size === 0) return rows

  let changed = false
  const nextRows = rows.map((row) => {
    const lotId = typeof row.lotId === 'string' ? row.lotId : ''
    if (!lotId) return row

    const nextStage = stageByLotId.get(lotId)
    if (!nextStage) return row
    if (row.stage === nextStage) return row

    changed = true
    return { ...row, stage: nextStage }
  })

  return changed ? nextRows : rows
}
